import { network } from '../lib/network';

// Master/reference data (damage severity/type/area) changes rarely, so it's
// worth caching, but it must still expire - otherwise a stale list can be
// served forever from a browser that never clears localStorage.
const DAMAGE_MASTERS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export const DamageService = {
  getDamages: (inspectionId: any, signal?: AbortSignal) => {
    return network.get(`/inspections/${encodeURIComponent(inspectionId)}/damages`, { signal });
  },
  getDamagesByImageUri: (inspectionId: string, imageURI: string, signal?: AbortSignal) => {
    return network.get(`/inspections/${encodeURIComponent(inspectionId)}/damages/by-image-uri`, {
      params: { imageURI },
      signal,
    });
  },
  createInspectionDamage: (inspectionId: string, params?: any, signal?: AbortSignal) => {
    return network.post(`/inspections/${encodeURIComponent(inspectionId)}/damages/multiple`, { ...params }, { signal });
  },
  approveInspectionDamage: (inspectionId: string, damageId?: string, params?: any, signal?: AbortSignal) => {
    return network.post(
      `/inspections/${encodeURIComponent(inspectionId)}/damages/${encodeURIComponent(String(damageId))}/approve`,
      { ...params },
      { signal }
    );
  },
  updateInspectionDamage: (inspectionId: string, damageId: string, params?: any, signal?: AbortSignal) => {
    return network.patch(
      `/inspections/${encodeURIComponent(inspectionId)}/damages/${encodeURIComponent(damageId)}`,
      { ...params },
      { signal }
    );
  },
  deleteInspectionDamage: (inspectionId: string, damageId?: string, params?: any, signal?: AbortSignal) => {
    return network.delete(`/inspections/${encodeURIComponent(inspectionId)}/damages/${encodeURIComponent(String(damageId))}`, {
      ...params,
      signal,
    });
  },
  getDamageMasters: async (inspectionId: string, params?: any) => {
    const cacheKey = `damageMasters_${inspectionId}_${JSON.stringify(params)}`;

    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { data, cachedAt } = JSON.parse(cached);
        if (typeof cachedAt === 'number' && Date.now() - cachedAt < DAMAGE_MASTERS_CACHE_TTL_MS) {
          return data;
        }
      } catch (parseError) {
        // Corrupted or pre-TTL cache entry - fall through and refetch.
        console.error('Failed to parse cached damage masters', parseError);
      }
    }

    const response = await network.get(`/masters`, { params });

    try {
      localStorage.setItem(cacheKey, JSON.stringify({ data: response.data, cachedAt: Date.now() }));
    } catch (cacheError) {
      // Cache write failures (quota exceeded, Safari private mode, etc.) must
      // never fail a fetch that already succeeded.
      console.error('Failed to cache damage masters', cacheError);
    }

    return response.data;
  },
};
